import { create } from 'zustand';
import { appAxios } from '@/network/base/appAxios';

const UNASSIGNED_PAGE_SIZE = 6;

const initialState = {
  projects: [],
  permitChatGroups: [],
  customers: [],
  messages: [],
  activePermitChatID: null,
  projectsLastFetched: null,
  customersLastFetched: null,
  permitChatGroupsLastFetched: null,
  unassignedChats: [],
  unassignedPage: 1,
  unassignedLoading: false,
  hasMoreUnassigned: true,
  unassignedSearchText: null,
};

const actions = (set, get) => ({
  getProjects: async (force = false) => {
    const { projectsLastFetched, projects } = get();
    const cacheTime = 30000;

    if (!force && projects.length > 0 && projectsLastFetched && Date.now() - projectsLastFetched < cacheTime) {
      return;
    }

    try {
      const res = await appAxios.get('/Project/v1.0/getProjects');
      set({ projects: res.data.data || [], projectsLastFetched: Date.now() });
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  },

  getPermitChatGroups: async (searchText = null, force = false) => {
    const { permitChatGroupsLastFetched, permitChatGroups } = get();
    const cacheTime = 30000;

    if (!force && searchText === null && permitChatGroups.length > 0 && permitChatGroupsLastFetched && Date.now() - permitChatGroupsLastFetched < cacheTime) {
      return;
    }

    try {
      const res = await appAxios.get(`/PermitChatGroup/v1.0/getPermitChatGroupsbyUserId`, {
        params: {
          searchText,
        },
      });
      set({
        permitChatGroups: res.data.data || [],
        permitChatGroupsLastFetched: searchText === null ? Date.now() : get().permitChatGroupsLastFetched
      });
    } catch (error) {
      console.error('Failed to fetch permit chat groups:', error);
    }
  },

  getMessagesByActivePermitChatID: async () => {
    const { activePermitChatID } = get();
    if (!activePermitChatID) return;

    try {
      const res = await appAxios.get(`/history?permitChatId=${activePermitChatID}`);
      set({ messages: res.data || [] });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  },

  loadMoreUnassigned: async (searchText = null, isNewSearch = false) => {
    const { unassignedLoading, unassignedPage, unassignedChats } = get();
    if (unassignedLoading) return;

    if (isNewSearch) {
      set({ unassignedChats: [], unassignedPage: 1, hasMoreUnassigned: true, unassignedSearchText: searchText });
    }

    set({ unassignedLoading: true });

    try {
      const page = isNewSearch ? 1 : unassignedPage;
      const params = { page, limit: UNASSIGNED_PAGE_SIZE };
      if (searchText && searchText.trim() !== '') {
        params.searchText = searchText;
      }
      const response = await appAxios.get('/ProtectedPermitChat/v1.0/getUnassignedChats', { params });
      const { data, pagination } = response.data.data;

      set(state => ({
        unassignedChats: isNewSearch ? data : [...state.unassignedChats, ...data],
        unassignedPage: isNewSearch ? 2 : state.unassignedPage + 1,
        hasMoreUnassigned: pagination.hasNextPage,
      }));
    } catch (error) {
      console.error('Failed to fetch unassigned chats:', error);
    } finally {
      set({ unassignedLoading: false });
    }
  },

  createChat: async () => {
    try {
      const res = await appAxios.post('/ProtectedPermitChat/v1.0/createChat');
      set({ activePermitChatID: res.data?.data?.permitChat?._id });
      const permitChat = res.data?.data?.permitChat;
      const project = res.data?.data?.project;

      if (permitChat) {
        set(state => ({
          permitChatGroups: [permitChat, ...state.permitChatGroups],
          permitChatGroupsLastFetched: Date.now(),
          unassignedChats: [permitChat, ...state.unassignedChats],
        }));
      }

      if (project) {
        set(state => ({
          projects: [project, ...state.projects],
          projectsLastFetched: Date.now()
        }));
      }

      return res.data?.data;
    } catch (error) {
      console.error('Failed to create chat:', error);
      return false;
    }
  },

  getProjectIdByPermitChatId: permitChatId => {
    const { permitChatGroups } = get();
    const permitChat = permitChatGroups?.find(chat => chat._id === permitChatId);
    return permitChat?.projectId || null;
  },

  ensurePermitChatGroupsLoaded: async () => {
    const { permitChatGroups, getPermitChatGroups } = get();
    if (!permitChatGroups || permitChatGroups.length === 0) {
      await getPermitChatGroups();
    }
    return get().permitChatGroups;
  },

  getCustomers: async (force = false) => {
    const { customers, customersLastFetched } = get();
    const cacheTime = 30000;

    if (
      !force &&
      customers.length > 0 &&
      customersLastFetched &&
      Date.now() - customersLastFetched < cacheTime
    ) {
      return;
    }

    try {
      const res = await appAxios.get('/Customer/v1.0/get-customers');
      set({
        customers: res.data.data || [],
        customersLastFetched: Date.now(),
      });
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  },

  createCustomer: async (payload) => {
    try {
      const res = await appAxios.post('/Customer/v1.0/add-customer', payload);

      const newCustomer = res.data?.data;

      if (newCustomer) {
        set({
          customers: [newCustomer, ...get().customers],
          customersLastFetched: Date.now(),
        });
      }

      return newCustomer;
    } catch (error) {
      console.error('Failed to create customer:', error);
      return false;
    }
  },

  getProjectsByCustomer: async (customerId, force = true) => {
    try {
      const res = await appAxios.get(
        `/Customer/v1.0/getCustomerProjects`,
        { params: { customerId } }
      );

      set({
        projects: res.data.data || [],
        projectsLastFetched: Date.now(),
      });
    } catch (error) {
      console.error('Failed to fetch projects by customer:', error);
    }
  },

  setActivePermitChatID: id => set({ activePermitChatID: id }),
  clearMessages: () => set({ messages: [] }),
});

const getters = (_, get) => ({
  hasActiveChat: () => !!get().activePermitChatID,
  activeChatMessages: () => get().messages,

  activePermitChat: () => {
    const { permitChatGroups, activePermitChatID } = get();
    if (!activePermitChatID || !permitChatGroups) return null;

    return permitChatGroups.find(permit => permit._id === activePermitChatID) || null;
  },
  defaultPermitChatId: () => {
    const { permitChatGroups } = get();
    return permitChatGroups.length === 1 ? permitChatGroups[0]._id : null;
  },
});

export const useProjectStore = create((set, get) => ({
  ...initialState,
  ...actions(set, get),
  ...getters(set, get),
}));
